import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  ShieldCheck, RefreshCw, AlertTriangle, CheckCircle, Eye, Send,
  X, Search, FileText, History, ClipboardList, Layers, Save, UploadCloud,
  Upload, Camera, Loader, Pencil, DownloadCloud, Mail, MessageSquare, Clock,
} from 'lucide-react';
import { DMS_STATUS, DMS_TYPES, DMS_GROUPS, isStudentVisa } from '../config/dmsTypes';
import { uprightJpegBytes } from '../utils/imageOrient';
import VisaDetailsPanel from '../components/VisaDetailsPanel';

// Admin fleet compliance dashboard — the office side of the driver DMS. Shows a
// drivers × document-types matrix, an "attention" worklist, and a per-driver
// drill-down where the office can view a document (audited) or request a renewal.
// Identity numbers are masked here; only the owning driver sees them in full.
// Admin-only (route + nav gated), and excluded from team-leader/viewer visibility.

const API = '/.netlify/functions/driver-dms-sb';

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}
function daysUntil(d) {
  if (!d) return null;
  const t = new Date(d + 'T00:00:00').getTime();
  return t ? Math.round((t - Date.now()) / 86400000) : null;
}
async function call(payload) {
  const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  return res.json();
}

// Contractor uploads live in the My Docs store (driver-docs-sb), not the DMS.
const DOCS_API = '/.netlify/functions/driver-docs-sb';
async function callDocs(payload) {
  const res = await fetch(DOCS_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  return res.json();
}
// View an uploaded document (admin, not audited by the DMS — served from driver-docs).
async function viewUploadInNewTab(id, onErr) {
  const w = window.open('', '_blank');
  try {
    const d = await callDocs({ action: 'getAssignmentFile', id });
    if (!d || !d.success) throw new Error(d?.error || 'Could not open');
    const bytes = Uint8Array.from(atob(d.base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: d.contentType || 'application/pdf' }));
    if (w) w.location.href = url; else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { if (w) w.close(); onErr?.(e.message); }
}

// Open a document's decrypted bytes in a new tab (admin view = audited server-side).
// Opens the blank tab synchronously first so the popup blocker doesn't eat it.
async function viewDocInNewTab(id, onErr) {
  const w = window.open('', '_blank');
  try {
    const d = await call({ action: 'getDocFile', id });
    if (!d || !d.success) throw new Error(d?.error || 'Could not open');
    const bytes = Uint8Array.from(atob(d.base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: d.contentType || 'application/pdf' }));
    if (w) w.location.href = url; else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { if (w) w.close(); onErr?.(e.message); }
}

// ── file normalisation for admin uploads (mirrors MyDmsPortalTab.fileToPage) ─────
// Cap the ENCODED payload (base64 inflates ~33%) against Netlify's 6 MB body limit.
const MAX_B64 = 5_000_000;
const SUPPORTED_IMG = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);
function abToB64(buf) {
  let bin = ''; const bytes = new Uint8Array(buf); const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(bin);
}
// <img>+canvas fallback for files createImageBitmap can't decode (Safari HEIC).
function imgToJpegArrayBuffer(file, maxEdge = 2000, quality = 0.85) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => {
      try {
        const scale = Math.min(1, maxEdge / Math.max(im.naturalWidth, im.naturalHeight));
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(im.naturalWidth * scale));
        c.height = Math.max(1, Math.round(im.naturalHeight * scale));
        c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
        c.toBlob((b) => { URL.revokeObjectURL(url); b ? b.arrayBuffer().then(resolve).catch(() => resolve(null)) : resolve(null); }, 'image/jpeg', quality);
      } catch { URL.revokeObjectURL(url); resolve(null); }
    };
    im.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    im.src = url;
  });
}
function packPage(buf, contentType, filename) {
  const base64 = abToB64(buf);
  if (base64.length > MAX_B64) throw new Error('That file is too big to send. Use a smaller PDF or a re-taken photo.');
  return { base64, contentType, filename };
}
async function fileToPage(file) {
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '')) {
    return packPage(await file.arrayBuffer(), 'application/pdf', file.name || 'document.pdf');
  }
  let buf = await uprightJpegBytes(file, 2000, 0.85);
  let contentType = 'image/jpeg';
  if (!buf) buf = await imgToJpegArrayBuffer(file);
  if (!buf) {
    if (SUPPORTED_IMG.has(file.type)) { buf = await file.arrayBuffer(); contentType = file.type; }
    else throw new Error(`This file is in a format we can't read (${file.type || 'unknown'}). Use a JPEG/PNG or a PDF.`);
  }
  return packPage(buf, contentType, (file.name || 'photo.jpg').replace(/\.[^.]+$/, '') + '.jpg');
}

const CELL_COLOR = {
  ok: 'var(--hp-green)', expiring: '#c9a84c', expired: 'var(--hp-red)',
  renewal: '#3b82f6', missing: 'var(--hp-border)',   // requested = blue (distinct from gold expiring)
};

// Items the office can pull straight from existing app data (driver record / Fleet).
// (vehicle_rego is verified online instead — see onlineCheck / Check Rego.)
const POPULATABLE = new Set(['company_details', 'primary_address', 'vehicle_insurance']);

// Yellow shade marking a document that is NOT required for this driver.
const NOT_REQ_BG = 'rgba(226, 194, 76, 0.16)';
const NOT_REQ_BORDER = 'rgba(226, 194, 76, 0.55)';

export default function CompliancePage() {
  const { addNotification } = useApp();
  const [data, setData] = useState(null);       // { drivers, types, requirements, defaultRequired }
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [openDriver, setOpenDriver] = useState(null);  // login
  const [tab, setTab] = useState('overview');   // overview | requirements | bytype | uploads
  // Which population we're looking at. Drivers are subcontractors; night sorters are
  // PAYG employees in a separate registry with a different required-document set.
  // The document store itself is shared — a dual-role person has ONE set of documents.
  const [audience, setAudience] = useState('driver');   // driver | sorter
  // Documents are collected during ONBOARDING, before someone is switched on — and
  // night sorters stay inactive until the go-live cutover. So default this on for
  // sorters, or the board is empty exactly when it's needed.
  const [includeInactive, setIncludeInactive] = useState(false);
  const [rolloutOpen, setRolloutOpen] = useState(false);   // citizenship ACC/Evidence rollout modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await call({ action: 'listCompliance', audience, includeInactive });
      if (d && d.success) setData({ audience: d.audience || audience, drivers: d.drivers || [], types: d.types || [], requirements: d.requirements || {}, defaultRequired: d.defaultRequired || [] });
      else addNotification?.(d?.error || 'Could not load compliance data', 'error');
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setLoading(false); }
  }, [addNotification, audience, includeInactive]);

  useEffect(() => { load(); }, [load]);

  // Deep-link from the Immigration page ("Manage →") opens that driver's drawer.
  const location = useLocation();
  useEffect(() => { if (location.state && location.state.openDriver) setOpenDriver(location.state.openDriver); }, [location.state]);
  // ?driver= deep link from the global search palette: filter the register and open the drawer.
  const [searchParams] = useSearchParams();
  const driverParam = (searchParams.get('driver') || '').toUpperCase();
  // ?tab= from the Onboarding task ("request the documents he hasn't supplied") lands
  // on the Requirements tab, not the overview — that step is about choosing and
  // pushing a set, and the overview can do neither.
  const tabParam = (searchParams.get('tab') || '').trim();
  useEffect(() => {
    if (['overview', 'requirements', 'bytype', 'uploads'].includes(tabParam)) setTab(tabParam);
  }, [tabParam]);
  // The driver drawer would cover the tab it was asked for, so open it only when the
  // link didn't name a tab; when it did, the driver is passed into that tab instead.
  useEffect(() => {
    if (!driverParam) return;
    setFilter(driverParam);
    if (!tabParam || tabParam === 'overview') setOpenDriver(driverParam);
  }, [driverParam, tabParam]);

  // Driver-sourced changes awaiting the office's acknowledgement (DMS renewals/edits).
  const [pending, setPending] = useState([]);
  const loadPending = useCallback(async () => {
    const d = await call({ action: 'getPendingChanges', audience });
    if (d && d.success) setPending(d.pending || []);
  }, [audience]);
  useEffect(() => { loadPending(); }, [loadPending]);
  const acknowledge = async (key) => {
    const d = await call({ action: 'acknowledgeChange', key });
    if (d && d.success) setPending((p) => p.filter((x) => x.key !== key));
    else addNotification?.(d?.error || 'Could not acknowledge', 'error');
  };

  // Follow-up reminder: immediately (re-)send SMS + email to a contractor for a
  // specific document. Repeatable — chases someone who hasn't uploaded yet.
  const [nudging, setNudging] = useState('');
  const rePush = async (login, typeKey, name) => {
    const busyKey = `${login}:${typeKey}`;
    setNudging(busyKey);
    try {
      const d = await call({ action: 'nudgeRenewal', driverLogin: login, type: typeKey });
      if (d && d.success) {
        const bits = [d.sms ? 'SMS' : '', d.email ? 'email' : ''].filter(Boolean).join(' + ');
        addNotification?.(bits ? `Reminder sent to ${name} (${bits})` : `No mobile or email on file for ${name} — portal reminder set`, bits ? 'success' : 'error');
      } else addNotification?.(d?.error || 'Could not send reminder', 'error');
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setNudging(''); }
  };

  const types = data?.types || [];
  const drivers = useMemo(() => {
    const list = data?.drivers || [];
    if (!filter) return list;
    const f = filter.toLowerCase();
    return list.filter(d => d.login.toLowerCase().includes(f) || (d.name || '').toLowerCase().includes(f));
  }, [data, filter]);

  // Attention worklist: every expired / expiring / renewal cell, soonest first.
  const worklist = useMemo(() => {
    const rows = [];
    (data?.drivers || []).forEach(d => {
      types.forEach(t => {
        const cell = d.items[t.key];
        if (cell && (cell.status === 'expired' || cell.status === 'expiring' || cell.status === 'renewal')) {
          rows.push({ login: d.login, name: d.name, type: t, status: cell.status, expiryDate: cell.expiryDate });
        }
      });
    });
    return rows.sort((a, b) => {
      const rank = { expired: 0, renewal: 1, expiring: 2 };
      if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
      return (a.expiryDate || '9999').localeCompare(b.expiryDate || '9999');
    });
  }, [data, types]);

  const summary = useMemo(() => {
    const list = data?.drivers || [];
    let clean = 0, attention = 0;
    list.forEach(d => {
      const c = d.counts || {};
      if ((c.expired || 0) + (c.expiring || 0) + (c.renewal || 0) > 0) attention++;
      else if ((c.missing || 0) === 0) clean++;
    });
    return { total: list.length, clean, attention };
  }, [data]);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1280 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <ShieldCheck size={26} color="var(--hp-gold)" />
        <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '1.7rem', color: 'var(--hp-text)' }}>Compliance</h1>
        <button onClick={load} style={ghostBtn} title="Refresh"><RefreshCw size={15} /> Refresh</button>
        <button onClick={() => setRolloutOpen(true)} style={ghostBtn} title="Re-read every citizenship certificate on file to pull the ACC + Evidence numbers">
          <Layers size={15} /> Citizenship ACC rollout
        </button>
      </div>
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.86rem', margin: '0 0 16px', maxWidth: 720 }}>
        {audience === 'sorter'
          ? 'Night sorter document status. Sorters are PAYG employees, so the required set is the employment pack (TFN declaration, super choice, Fair Work statements) plus identity — no vehicle or ABN documents.'
          : 'Driver document status across the fleet.'}{' '}
        Numbers are masked here — the full document is visible on the person's own portal, and every view you make is logged.
      </p>

      {/* Audience switch — two registries, one document store */}
      <div style={{ display: 'inline-flex', gap: 2, padding: 3, marginBottom: 20, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-panel, transparent)' }}>
        {[{ k: 'driver', label: 'Drivers' }, { k: 'sorter', label: 'Night sorters' }].map((a) => (
          <button key={a.k} onClick={() => { setAudience(a.k); setOpenDriver(null); setIncludeInactive(a.k === 'sorter'); }}
            style={{
              padding: '6px 16px', border: 'none', borderRadius: 'calc(var(--radius) - 2px)', cursor: 'pointer',
              fontSize: '0.82rem', fontWeight: 700, fontFamily: 'inherit',
              background: audience === a.k ? 'var(--hp-gold)' : 'transparent',
              color: audience === a.k ? '#1a1a1a' : 'var(--hp-dim)',
            }}>{a.label}</button>
        ))}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 12px 0 14px', fontSize: '0.78rem', color: 'var(--hp-dim)', cursor: 'pointer', borderLeft: '1px solid var(--hp-border)', marginLeft: 6 }}
          title="Documents are collected during onboarding, before someone is switched on">
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          Include not-yet-active
        </label>
      </div>

      {/* Summary tiles */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 22 }}>
        <Tile label={audience === 'sorter' ? 'Night sorters' : 'Drivers'} value={summary.total} color="var(--hp-text)" icon={<FileText size={16} />} />
        <Tile label="Fully compliant" value={summary.clean} color="var(--hp-green)" icon={<CheckCircle size={16} />} />
        <Tile label="Need attention" value={summary.attention} color={summary.attention ? 'var(--hp-red)' : 'var(--hp-dim)'} icon={<AlertTriangle size={16} />} />
      </div>

      {/* Pending-acknowledgement banner (visible from any tab) */}
      {pending.length > 0 && tab !== 'overview' && (
        <button onClick={() => setTab('overview')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '10px 14px', marginBottom: 16, border: '1px solid var(--hp-gold)', borderRadius: 'var(--radius)', background: 'rgba(201,168,76,0.10)', color: 'var(--hp-text)', cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700 }}>
          <UploadCloud size={15} color="var(--hp-gold)" />
          {pending.length} contractor {pending.length === 1 ? 'change' : 'changes'} to acknowledge
          <span style={{ marginLeft: 'auto', color: 'var(--hp-dim)', fontWeight: 500 }}>Review in Overview →</span>
        </button>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--hp-border)', marginBottom: 22 }}>
        <TabBtn active={tab === 'overview'} onClick={() => setTab('overview')} icon={<ShieldCheck size={15} />} label="Overview" />
        <TabBtn active={tab === 'requirements'} onClick={() => setTab('requirements')} icon={<ClipboardList size={15} />} label="Requirements" />
        <TabBtn active={tab === 'bytype'} onClick={() => setTab('bytype')} icon={<Layers size={15} />} label="By document type" />
        <TabBtn active={tab === 'uploads'} onClick={() => setTab('uploads')} icon={<UploadCloud size={15} />} label="Uploads" />
      </div>

      {loading && <div style={{ color: 'var(--hp-dim)' }}><RefreshCw size={15} className="spin" /> Loading…</div>}

      {!loading && data && tab === 'overview' && (
        <>
          {/* Driver-sourced changes awaiting acknowledgement */}
          {pending.length > 0 && (
            <div style={{ marginBottom: 26 }}>
              <h2 style={sectionH}>Needs acknowledgement</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {pending.map((p) => (
                  <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '1px solid var(--hp-border)', borderLeft: '3px solid var(--hp-gold)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)' }}>
                    <UploadCloud size={15} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--hp-text)', minWidth: 150 }}>{p.driverName}</span>
                    <span style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', flex: 1 }}>{p.label}</span>
                    <span style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', whiteSpace: 'nowrap' }}>
                      {p.kind === 'upload' ? 'Uploaded renewal' : p.kind === 'visa' ? 'Edited visa details' : 'Edited details'}
                    </span>
                    {p.kind === 'upload' && p.docId && (
                      <button onClick={() => viewDocInNewTab(p.docId, (m) => addNotification?.(m, 'error'))} style={miniBtn}><Eye size={13} /> View</button>
                    )}
                    <button onClick={() => acknowledge(p.key)} style={miniBtn}><CheckCircle size={13} /> Acknowledge</button>
                    <button onClick={() => setOpenDriver(p.driverLogin)} style={miniBtn}>Open</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attention worklist */}
          {worklist.length > 0 && (
            <div style={{ marginBottom: 26 }}>
              <h2 style={sectionH}>Needs attention</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {worklist.slice(0, 40).map((w, i) => {
                  const dU = daysUntil(w.expiryDate);
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '1px solid var(--hp-border)', borderLeft: `3px solid ${CELL_COLOR[w.status]}`, borderRadius: 'var(--radius)', background: 'var(--hp-surface)' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--hp-text)', minWidth: 150 }}>{w.name}</span>
                      <span style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', flex: 1 }}>{w.type.label}</span>
                      <StatusChip status={w.status} />
                      <span style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', minWidth: 130, textAlign: 'right' }}>
                        {w.status === 'renewal' ? 'Renewal requested' : w.expiryDate ? `${w.status === 'expired' ? 'Expired' : 'Expires'} ${fmtDate(w.expiryDate)}${dU != null ? ` (${dU < 0 ? `${-dU}d ago` : `${dU}d`})` : ''}` : ''}
                      </span>
                      <button onClick={() => rePush(w.login, w.type.key, w.name)} disabled={nudging === `${w.login}:${w.type.key}`} style={miniBtn} title="Send the contractor an SMS + email reminder to upload this document">
                        <Send size={13} /> {nudging === `${w.login}:${w.type.key}` ? 'Sending…' : 'Re-push'}
                      </button>
                      <button onClick={() => setOpenDriver(w.login)} style={miniBtn}>Open</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Matrix */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <h2 style={{ ...sectionH, marginBottom: 0 }}>{audience === 'sorter' ? 'All night sorters' : 'All drivers'}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '5px 9px', background: 'var(--hp-surface)' }}>
              <Search size={13} color="var(--hp-dim)" />
              <input value={filter} onChange={e => setFilter(e.target.value)} placeholder={audience === 'sorter' ? 'Filter sorters…' : 'Filter drivers…'}
                style={{ border: 'none', background: 'none', color: 'var(--hp-text)', fontSize: '0.82rem', outline: 'none', width: 150 }} />
            </div>
            <Legend />
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--hp-surface)', zIndex: 1 }}>{audience === 'sorter' ? 'Sorter' : 'Driver'}</th>
                  {types.map(t => (
                    <th key={t.key} style={{ ...th, writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 120, whiteSpace: 'nowrap' }} title={t.label}>
                      {t.label}{t.optional ? ' *' : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {drivers.map(d => (
                  <tr key={d.login} style={{ cursor: 'pointer' }} onClick={() => setOpenDriver(d.login)}>
                    <td style={{ ...td, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--hp-bg)', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700, color: 'var(--hp-text)' }}>
                        {d.login}
                        {d.active === false && (
                          <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: 'rgba(226,194,76,0.16)', border: '1px solid rgba(226,194,76,0.45)', color: 'var(--hp-dim)', fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.03em' }}>NOT ACTIVE</span>
                        )}
                      </div>
                      {d.name && d.name !== d.login && <div style={{ fontSize: '0.72rem', fontWeight: 500, color: 'var(--hp-dim)' }}>{d.name}</div>}
                    </td>
                    {types.map(t => {
                      const cell = d.items[t.key] || { status: 'missing' };
                      const supplied = cell.status !== 'missing';
                      // Yellow ONLY when not required AND nothing on file. If a driver
                      // supplies it anyway we keep it and show its real (green) status.
                      const notReqEmpty = !(d.required || []).includes(t.key) && !supplied;
                      return (
                        <td key={t.key} style={{ ...td, background: notReqEmpty ? NOT_REQ_BG : undefined }} title={`${t.label}: ${notReqEmpty ? 'not required' : cell.status}${cell.expiryDate ? ' · ' + fmtDate(cell.expiryDate) : ''}`}>
                          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 4, background: notReqEmpty ? 'transparent' : CELL_COLOR[cell.status], border: notReqEmpty ? '1px dashed ' + NOT_REQ_BORDER : cell.status === 'missing' ? '1px solid var(--hp-border)' : 'none' }} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {!drivers.length && <tr><td colSpan={types.length + 1} style={{ ...td, color: 'var(--hp-dim)', padding: 20 }}>No drivers match.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && data && tab === 'requirements' && (
        <RequirementsTab data={data} reload={load} addNotification={addNotification} openDrawer={setOpenDriver} initialDriver={driverParam} />
      )}

      {!loading && data && tab === 'bytype' && (
        <ByTypeTab data={data} reload={load} addNotification={addNotification} openDrawer={setOpenDriver} />
      )}

      {tab === 'uploads' && (
        <UploadsTab addNotification={addNotification} openDrawer={setOpenDriver} />
      )}

      {openDriver && (
        <DriverDrawer login={openDriver} types={types} onClose={() => setOpenDriver(null)} reload={load} addNotification={addNotification} />
      )}

      {rolloutOpen && (
        <CitizenshipRolloutModal onClose={() => setRolloutOpen(false)} addNotification={addNotification} onApplied={load} />
      )}
    </div>
  );
}

// ── Citizenship ACC/Evidence rollout ──────────────────────────────────────────
// Bulk-re-reads every citizenship certificate on file (server-side OCR of the stored,
// encrypted image) with the current ACC-pinned prompt, and lets the office confirm
// before saving — the same review step done for one record, batched across the fleet.
// Rows where no ACC is read are the ones whose REAR page isn't on file: ticking
// "rear page needed" flags the driver's portal + the office view to chase the back.
function CitizenshipRolloutModal({ onClose, addNotification, onApplied }) {
  const [stage, setStage] = useState('idle');   // idle | reading | review | applying
  const [rows, setRows] = useState([]);         // { …preview, save, rearPageNeeded, acc, evidence }
  const [err, setErr] = useState('');

  const read = async () => {
    setStage('reading'); setErr('');
    try {
      const d = await call({ action: 'previewCitizenshipReread' });
      if (!d || !d.success) throw new Error(d?.error || 'Could not read the certificates');
      const mapped = (d.records || []).map((r) => ({
        ...r,
        acc: r.accNumber || '',
        // When no ACC is read, the existing (non-ACC) number was almost certainly the
        // evidence / stock number off the front — carry it into the Evidence field and
        // leave ACC blank for when the rear page arrives, rather than stranding it in the
        // document-number slot.
        evidence: r.evidenceNumber || r.currentEvidence || (!r.accFound ? r.currentDocNumber : '') || '',
        // Default: save where an ACC came back; otherwise flag the rear page as needed.
        save: r.accFound,
        rearPageNeeded: !r.accFound,
      }));
      setRows(mapped);
      setStage('review');
    } catch (e) { setErr(e.message); setStage('idle'); }
  };

  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const apply = async () => {
    // Only rows the office ticked to save, plus any explicitly flagged as rear-needed.
    const items = rows
      .filter((r) => r.save || r.rearPageNeeded)
      // save → write the ACC as the document number; otherwise CLEAR it (send '') so the
      // old non-ACC number leaves the document-number slot — it's carried in evidence now.
      .map((r) => ({ id: r.id, docNumber: r.save ? r.acc : '', evidenceNumber: r.evidence || undefined, rearPageNeeded: !!r.rearPageNeeded }));
    if (!items.length) { addNotification?.('Nothing selected to apply.', 'error'); return; }
    setStage('applying'); setErr('');
    try {
      const d = await call({ action: 'applyCitizenshipReread', items });
      if (!d || !d.success) throw new Error(d?.error || 'Apply failed');
      const okN = (d.results || []).filter((x) => x.ok).length;
      const badN = (d.results || []).length - okN;
      addNotification?.(`Citizenship rollout: ${okN} record${okN === 1 ? '' : 's'} updated${badN ? `, ${badN} failed` : ''}.`, badN ? 'error' : 'success');
      onApplied && onApplied();
      onClose();
    } catch (e) { setErr(e.message); setStage('review'); }
  };

  const th = { textAlign: 'left', padding: '6px 8px', fontSize: '0.66rem', fontWeight: 800, color: 'var(--hp-dim)', letterSpacing: '0.03em', textTransform: 'uppercase', borderBottom: '1px solid var(--hp-border)', whiteSpace: 'nowrap' };
  const td = { padding: '6px 8px', fontSize: '0.78rem', color: 'var(--hp-text)', borderBottom: '1px solid var(--hp-border)', verticalAlign: 'middle' };
  const cellInput = { ...editorInput, padding: '5px 8px', fontSize: '0.78rem', fontFamily: 'var(--font-mono, monospace)' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 210, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 16px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(920px, 100%)', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', color: 'var(--hp-text)', fontSize: '1.2rem' }}>Citizenship ACC / Evidence rollout</h2>
          <button onClick={onClose} style={ghostBtn}><X size={16} /></button>
        </div>
        <p style={{ color: 'var(--hp-dim)', fontSize: '0.8rem', margin: '0 0 14px', lineHeight: 1.5 }}>
          Re-reads every citizenship certificate on file with the current OCR (the ACC number is filed as the document number; the Evidence number is kept alongside). Review each row, then apply — records with no ACC are missing their rear page.
        </p>
        {err && <div style={{ color: 'var(--hp-red)', fontSize: '0.78rem', marginBottom: 10 }}>{err}</div>}

        {stage === 'idle' && (
          <button onClick={read} style={{ ...ghostBtn, background: 'var(--hp-gold)', color: '#1a1a2e', borderColor: 'transparent' }}>
            <RefreshCw size={14} /> Re-read all citizenship certificates
          </button>
        )}
        {stage === 'reading' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hp-dim)', fontSize: '0.84rem', padding: '10px 0' }}>
            <Loader size={15} /> Reading every certificate… this runs OCR on each and can take a few seconds.
          </div>
        )}

        {(stage === 'review' || stage === 'applying') && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>
                  <th style={th}>Save</th><th style={th}>Driver</th><th style={th}>Current no.</th>
                  <th style={th}>ACC number</th><th style={th}>Evidence number</th><th style={th}>Rear needed</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} style={{ background: r.accFound ? 'transparent' : 'rgba(226,194,76,0.08)' }}>
                      <td style={td}><input type="checkbox" checked={r.save} disabled={!r.acc} onChange={(e) => setRow(r.id, { save: e.target.checked })} /></td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}><b>{r.login}</b><div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)' }}>{r.name}</div>
                        {r.ocrError && <div style={{ fontSize: '0.66rem', color: 'var(--hp-red)' }}>{r.ocrError}</div>}</td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)', color: 'var(--hp-dim)', whiteSpace: 'nowrap' }}>{r.currentDocNumber || '—'}</td>
                      <td style={td}><input value={r.acc} placeholder="— none read —" onChange={(e) => setRow(r.id, { acc: e.target.value, save: !!e.target.value.trim() })} style={cellInput} /></td>
                      <td style={td}><input value={r.evidence} placeholder="—" onChange={(e) => setRow(r.id, { evidence: e.target.value })} style={cellInput} /></td>
                      <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={r.rearPageNeeded} onChange={(e) => setRow(r.id, { rearPageNeeded: e.target.checked })} /></td>
                    </tr>
                  ))}
                  {!rows.length && <tr><td style={td} colSpan={6}>No citizenship records on file.</td></tr>}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={apply} disabled={stage === 'applying'} style={{ ...ghostBtn, background: 'var(--hp-gold)', color: '#1a1a2e', borderColor: 'transparent' }}>
                {stage === 'applying' ? <><Loader size={14} /> Applying…</> : <><Send size={14} /> Apply selected</>}
              </button>
              <button onClick={read} disabled={stage === 'applying'} style={ghostBtn}><RefreshCw size={14} /> Re-read again</button>
              <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
                {rows.filter((r) => r.save && r.acc).length} to save · {rows.filter((r) => r.rearPageNeeded).length} flagged for rear page
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── TFN vault status (office view) ─────────────────────────────────────────────
// The TFN lives ONLY in the encrypted vault, so the compliance card can't show it
// like the other captured fields. This reads the masked status (is one stored? the
// last 3?) via the sorter-vault getStatus op, and offers a reveal of the full
// number that is (a) gated by the vault admin secret and (b) written to the vault
// audit log. Auto-hides after 30s so it isn't left on screen.
function TfnVaultStatus({ login, addNotification }) {
  const [st, setSt] = useState(null);        // null = loading; { has, last3, updatedAt }
  const [open, setOpen] = useState(false);   // reveal panel open
  const [secret, setSecret] = useState('');
  const [shown, setShown] = useState('');
  const [busy, setBusy] = useState(false);

  const vault = useCallback(async (body, headers) => {
    const r = await fetch('/.netlify/functions/sorter-vault', {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...(headers || {}) },
      body: JSON.stringify(body),
    });
    return r.json();
  }, []);

  useEffect(() => {
    let live = true;
    vault({ op: 'getStatus', login })
      .then((d) => { if (live) setSt((d && d.success && d.tfn) || { has: false }); })
      .catch(() => { if (live) setSt({ has: false }); });
    return () => { live = false; };
  }, [vault, login]);

  const reveal = async () => {
    setBusy(true);
    try {
      const d = await vault({ op: 'revealTfn', login, by: 'compliance' }, { 'X-Hp-Vault-Admin': secret });
      if (d && d.success) { setShown(d.tfn); setSecret(''); setOpen(false); setTimeout(() => setShown(''), 30000); }
      else addNotification?.(d?.error === 'Not authorised.'
        ? 'Reveal needs the vault admin secret (HP_VAULT_ADMIN_SECRET) set in Netlify, and entered correctly here.'
        : (d?.error || 'Could not reveal the TFN'), 'error');
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(false); }
  };

  if (!st) return null;
  return (
    <div style={{ marginTop: 10, padding: '9px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'rgba(201,168,76,0.06)' }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
        <ShieldCheck size={13} color="var(--hp-gold)" />
        {st.has
          ? <span><b style={{ color: 'var(--hp-green)' }}>TFN in secure vault</b> ••• {st.last3}{st.updatedAt ? ' · stored ' + fmtDate((st.updatedAt || '').slice(0, 10)) : ''}</span>
          : <span>No TFN stored in the vault yet.</span>}
        {st.has && !shown && !open && (
          <button onClick={() => setOpen(true)} style={{ ...miniBtn, marginLeft: 'auto' }}><Eye size={12} /> Reveal</button>
        )}
        {shown && <span style={{ marginLeft: 'auto', fontWeight: 800, color: 'var(--hp-text)', letterSpacing: '0.04em' }}>{shown}</span>}
      </div>
      {shown && <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginTop: 4 }}>Hidden again shortly · this view is logged to the vault audit.</div>}
      {open && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Vault admin secret" autoComplete="off"
            style={{ ...editorInput, maxWidth: 220, padding: '6px 9px' }} />
          <button onClick={reveal} disabled={busy || !secret} style={{ ...miniBtn, background: 'var(--hp-gold)', color: '#1a1a2e', borderColor: 'transparent', opacity: (busy || !secret) ? 0.5 : 1 }}>{busy ? '…' : 'Show TFN'}</button>
          <button onClick={() => { setOpen(false); setSecret(''); }} style={miniBtn}>Cancel</button>
        </div>
      )}
    </div>
  );
}

// ── Per-driver drawer ──────────────────────────────────────────────────────────
function DriverDrawer({ login, types, onClose, reload, addNotification }) {
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState('');
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState(null);
  const [editing, setEditing] = useState('');   // type key whose upload/edit form is open

  const load = useCallback(async () => {
    const d = await call({ action: 'getDriverDms', driverLogin: login });
    if (d && d.success) setDocs(d.documents || []);
    else { setDocs([]); addNotification?.(d?.error || 'Could not load', 'error'); }
  }, [login, addNotification]);
  useEffect(() => { load(); }, [load]);

  const byType = {}; (docs || []).forEach(x => { byType[x.type] = x; });

  const view = async (item) => {
    setBusy(item.type + ':v');
    await viewDocInNewTab(item.id, (m) => addNotification?.(m, 'error'));
    setBusy('');
  };

  const requestRenewal = async (typeKey) => {
    setBusy(typeKey + ':r');
    try {
      const d = await call({ action: 'requestRenewal', driverLogin: login, type: typeKey });
      if (!d || !d.success) throw new Error(d?.error || 'Failed');
      addNotification?.('Renewal requested — the driver will be notified.', 'success');
      await load(); reload();
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };

  // Ask the driver to upload the BACK of their citizenship certificate (the ACC number
  // lives on the rear). Sets the portal advisory + red dot and sends SMS/email.
  const requestRear = async () => {
    setBusy('citizenship:rear');
    try {
      const d = await call({ action: 'requestRearPage', driverLogin: login });
      if (!d || !d.success) throw new Error(d?.error || 'Failed');
      const bits = [d.sms ? 'SMS' : '', d.email ? 'email' : ''].filter(Boolean).join(' + ');
      addNotification?.(bits ? `Rear-page request sent (${bits}) — flagged on the driver's portal.` : `No mobile or email on file — portal advisory set.`, bits ? 'success' : 'error');
      await load(); reload();
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };

  // One-click chase for the whole Student study pack (CoE/LOE, acceptance, timetable).
  const pushStudyPack = async () => {
    setBusy('studypack');
    try {
      const d = await call({ action: 'pushStudyPack', driverLogin: login });
      if (!d || !d.success) throw new Error(d?.error || 'Failed');
      addNotification?.(d.pushed
        ? `Study pack pushed — asked the driver for ${d.pushed} document${d.pushed === 1 ? '' : 's'}.`
        : 'Study pack already on file — nothing to push.', 'success');
      await load(); reload();
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };

  // One combined SMS + email reminding the driver to upload every outstanding
  // study-pack document (rather than one text per item).
  const nudgeStudyPack = async () => {
    setBusy('smsstudypack');
    try {
      const d = await call({ action: 'nudgeStudyPack', driverLogin: login });
      if (!d || !d.success) throw new Error(d?.error || 'Failed');
      if (!d.count) { addNotification?.('Study pack already on file — nothing to remind about.', 'success'); }
      else {
        const via = [d.sms && 'SMS', d.email && 'email'].filter(Boolean);
        addNotification?.(via.length
          ? `Reminder sent by ${via.join(' + ')} for ${d.count} document${d.count === 1 ? '' : 's'}.`
          : 'No contact on file — nothing was sent.', via.length ? 'success' : 'error');
      }
      await load(); reload();
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };

  // Pull an item from data already in the app (driver record / assigned Fleet vehicle).
  const populate = async (typeKey) => {
    setBusy(typeKey + ':p');
    try {
      const d = await call({ action: 'populateFromApp', driverLogin: login, type: typeKey });
      if (!d || !d.success) throw new Error(d?.error || 'Nothing to populate');
      addNotification?.(d.from === 'fleet'
        ? `Pulled from Fleet vehicle ${d.rego}${d.label ? ` — ${d.label}` : ''}.`
        : 'Populated from the driver’s profile.', 'success');
      await load(); reload();
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };

  // Verify vehicle registration online (NEVDIS) for the driver's assigned vehicle.
  const checkRego = async (typeKey) => {
    setBusy(typeKey + ':c');
    try {
      const d = await call({ action: 'checkRego', driverLogin: login, type: typeKey });
      if (!d || !d.success) throw new Error(d?.error || 'Rego check failed');
      addNotification?.(d.valid
        ? `${d.plate}: Registered${d.expiry ? ` — expires ${fmtDate(d.expiry)}` : ''}.`
        : `${d.plate}: ${d.regoStatus || 'not registered'} — please check.`, d.valid ? 'success' : 'error');
      await load(); reload();
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };

  // Re-read a document that's already on file through the current OCR (e.g. to pick
  // up a prompt fix) WITHOUT asking the driver to re-photograph it. Reads server-side
  // from the encrypted file, shows what it found, and on confirm writes the number +
  // any extra fields back onto the record.
  const reReadFromFile = async (type, item) => {
    if (!item || !item.id) return;
    setBusy(type.key + ':rr');
    try {
      const d = await call({ action: 'reocrStored', id: item.id });
      if (!d || !d.success) throw new Error(d?.error || 'Could not re-read the file');
      const fields = d.fields || {};
      const lines = [`${type.numberLabel || 'Document number'}: ${d.docNumber || '(none found)'}`];
      (type.extraFields || []).forEach(f => { if (fields[f.key]) lines.push(`${f.label}: ${fields[f.key]}`); });
      if (type.expiry && d.expiryDate) lines.push(`Expiry: ${fmtDate(d.expiryDate)}`);
      if (!window.confirm(`Re-read from the file:\n\n${lines.join('\n')}\n\nSave these to ${login}'s ${type.label}?`)) {
        addNotification?.('Re-read only — nothing saved.', 'success');
        return;
      }
      const a = await call({ action: 'reocrStored', id: item.id, apply: true, docNumber: d.docNumber, fields });
      if (!a || !a.success) throw new Error(a?.error || 'Save failed');
      addNotification?.(`${type.label} updated from the file.`, 'success');
      await load(); reload();
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };

  const loadLog = async () => {
    setShowLog(true);
    const d = await call({ action: 'listAccessLog', driverLogin: login });
    setLog(d && d.success ? d.log : []);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(560px, 96vw)', height: '100%', overflowY: 'auto', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', color: 'var(--hp-text)', fontSize: '1.3rem' }}>{login}</h2>
          <button onClick={onClose} style={ghostBtn}><X size={16} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {types.map(t => {
            const item = byType[t.key];
            const status = item ? item.status : 'missing';
            const notRequired = item ? item.required === false : false;
            // Yellow only when not required AND nothing supplied; a supplied copy shows
            // its real status (green) and is retained — just flagged "not required".
            const notReqEmpty = notRequired && status === 'missing';
            return (
              <div key={t.key} style={{ border: '1px solid var(--hp-border)', borderLeft: `3px solid ${notReqEmpty ? NOT_REQ_BORDER : CELL_COLOR[status]}`, borderRadius: 'var(--radius)', padding: 12, background: notReqEmpty ? NOT_REQ_BG : 'var(--hp-surface)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: 'var(--hp-text)', fontSize: '0.9rem' }}>{t.label}</span>
                  <StatusChip status={status} optional={t.optional} />
                  {notRequired && <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: '0.64rem', fontWeight: 800, color: '#a67c00', background: NOT_REQ_BG, border: `1px solid ${NOT_REQ_BORDER}` }}>NOT REQUIRED</span>}
                  {t.key === 'visa' && item && item.workRight && <WorkRightChip right={item.workRight} basis={item.workBasis} />}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 5 }}>
                  {t.onlineCheck ? (
                    item?.fields?.checkedAt ? (
                      <span>
                        {item.fields.regoStatus === 'REGISTERED'
                          ? <b style={{ color: 'var(--hp-green)' }}>✓ Registered</b>
                          : <b style={{ color: 'var(--hp-red)' }}>✗ {item.fields.regoStatus || 'Not registered'}</b>}
                        {item.docNumber && <span> · {item.docNumber}</span>}
                        {item.expiryDate && <span> · {status === 'expired' ? 'expired ' : 'expires '}{fmtDate(item.expiryDate)}</span>}
                        <span> · checked {new Date(item.fields.checkedAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        {/* The van was re-registered since that check and Fleet already
                            holds the new date — no need to buy the answer again. */}
                        {item.fields.fleetSyncedAt > item.fields.checkedAt && <span> · date from the Fleet record</span>}
                      </span>
                    ) : item?.expiryDate ? (
                      /* Never checked from HERE, but the Fleet record carries a date for the
                         van this driver is assigned — that IS the registration. Say so, so
                         nobody spends a second paid lookup on an answer we already hold. */
                      <span>
                        {item.docNumber && <span>{item.docNumber} · </span>}
                        {status === 'expired' ? 'expired ' : 'expires '}{fmtDate(item.expiryDate)}
                        <span> · from the Fleet record</span>
                      </span>
                    ) : <span>Not checked yet — verify the vehicle’s registration online.</span>
                  ) : (
                    <>
                      {item && item.docNumber && <span>No. {item.docNumber} · </span>}
                      {t.expiry && item?.expiryDate && <span>{status === 'expired' ? 'Expired ' : 'Expires '}{fmtDate(item.expiryDate)}</span>}
                      {t.kind === 'fields' && item && status === 'ok' && <span>{Object.values(item.fields || {}).filter(Boolean).slice(0, 2).join(' · ') || 'On file'}</span>}
                      {!item && <span>Not on file</span>}
                      {(t.extraFields || []).map(f => item?.fields?.[f.key] ? (
                        <span key={f.key}> · {f.label} {item.fields[f.key]}</span>
                      ) : null)}
                    </>
                  )}
                </div>
                {/* A digital-licence screenshot is not proof — never let it read as verified. */}
                {item?.fields?.sourceType === 'digital_screenshot' && (
                  <div style={{ fontSize: '0.7rem', color: '#b8860b', marginTop: 5, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700 }}>
                    <AlertTriangle size={11} /> Screenshot of a digital licence — not verified evidence
                  </div>
                )}
                {/* Citizenship: the ACC number is on the REAR. Flag when it hasn't been
                    captured so the office knows to chase the back page. */}
                {t.key === 'citizenship' && item?.fields?.rearPageNeeded && (
                  <div style={{ fontSize: '0.7rem', color: '#b8860b', marginTop: 5, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700 }}>
                    <AlertTriangle size={11} /> Rear page needed — ACC number is on the back of the certificate
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {t.onlineCheck ? (
                    /* Registration is verified online — the live check is the proof. */
                    <button onClick={() => checkRego(t.key)} disabled={busy === t.key + ':c'} style={{ ...miniBtn, background: 'var(--hp-gold)', color: '#1a1a2e', borderColor: 'transparent' }} title="Verify registration online (NEVDIS) for the assigned Fleet vehicle">
                      <RefreshCw size={12} /> {busy === t.key + ':c' ? 'Checking…' : (item?.fields?.checkedAt ? 'Re-check rego' : 'Check rego')}
                    </button>
                  ) : (
                    <>
                      {item && item.hasFile && (
                        <button onClick={() => view(item)} disabled={busy === t.key + ':v'} style={miniBtn}><Eye size={12} /> {busy === t.key + ':v' ? '…' : 'View'}</button>
                      )}
                      {/* Re-read the stored file with the current OCR (no re-photographing) —
                          e.g. to pick up a prompt fix — then save the numbers it finds. */}
                      {item && item.hasFile && t.kind === 'file' && !t.noNumber && (
                        <button onClick={() => reReadFromFile(t, item)} disabled={busy === t.key + ':rr'} style={miniBtn} title="Re-read the stored file with the current OCR and save the numbers it finds">
                          <RefreshCw size={12} /> {busy === t.key + ':rr' ? 'Reading…' : 'Re-read from file'}
                        </button>
                      )}
                      {/* Office can add / replace the document or record on the driver's behalf. */}
                      <button onClick={() => setEditing(editing === t.key ? '' : t.key)} style={{ ...miniBtn, ...(editing === t.key ? {} : { background: 'var(--hp-gold)', color: '#1a1a2e', borderColor: 'transparent' }) }}>
                        {t.kind === 'file' ? <Upload size={12} /> : <Pencil size={12} />}
                        {editing === t.key ? 'Close' : (item && (item.hasFile || status === 'ok') ? (t.kind === 'file' ? 'Replace' : 'Edit') : (t.kind === 'file' ? 'Add / Upload' : 'Enter details'))}
                      </button>
                      {/* Pull straight from data already held in the app. */}
                      {POPULATABLE.has(t.key) && (
                        <button onClick={() => populate(t.key)} disabled={busy === t.key + ':p'} style={miniBtn} title="Fill from the driver's profile / assigned Fleet vehicle">
                          <DownloadCloud size={12} /> {busy === t.key + ':p' ? '…' : 'Populate from app'}
                        </button>
                      )}
                      {t.kind === 'file' && (
                        <button onClick={() => requestRenewal(t.key)} disabled={busy === t.key + ':r' || item?.renewalRequested} style={{ ...miniBtn, opacity: item?.renewalRequested ? 0.5 : 1 }}>
                          <Send size={12} /> {item?.renewalRequested ? 'Requested' : busy === t.key + ':r' ? '…' : 'Request renewal'}
                        </button>
                      )}
                      {/* Citizenship rear page (ACC number) chase — sends a back-page-specific
                          SMS/email and flags the driver's portal. */}
                      {t.key === 'citizenship' && item && item.hasFile && item?.fields?.rearPageNeeded && (
                        <button onClick={requestRear} disabled={busy === 'citizenship:rear'} style={{ ...miniBtn, background: 'var(--hp-gold)', color: '#1a1a2e', borderColor: 'transparent' }} title="Ask the driver to upload the BACK of the certificate for the ACC number">
                          <UploadCloud size={12} /> {busy === 'citizenship:rear' ? 'Sending…' : 'Request rear page'}
                        </button>
                      )}
                      {/* Student visa → one-click chase for the whole study pack (CoE/LOE,
                          acceptance letter, timetable). Only shows once the visa is a Student. */}
                      {t.key === 'visa' && isStudentVisa(item?.fields) && (
                        <button onClick={pushStudyPack} disabled={busy === 'studypack'} style={{ ...miniBtn, background: 'var(--hp-gold)', color: '#1a1a2e', borderColor: 'transparent' }} title="Ask the driver for the Confirmation of Enrolment (CoE), Letter of Enrolment (LoE), college acceptance letter and study timetable">
                          <UploadCloud size={12} /> {busy === 'studypack' ? 'Pushing…' : 'Push study pack'}
                        </button>
                      )}
                      {t.key === 'visa' && isStudentVisa(item?.fields) && (
                        <button onClick={nudgeStudyPack} disabled={busy === 'smsstudypack'} style={miniBtn} title="Text + email the driver ONE reminder to upload every outstanding study-pack document">
                          <Send size={12} /> {busy === 'smsstudypack' ? 'Sending…' : 'SMS reminder'}
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* The TFN card is the one place the office checks the vault-held number. */}
                {t.key === 'tfn_declaration' && <TfnVaultStatus login={login} addNotification={addNotification} />}

                {editing === t.key && (t.kind === 'file'
                  ? <AdminFileEditor driverLogin={login} type={t} addNotification={addNotification}
                      onDone={() => { setEditing(''); load(); reload(); }} />
                  : <AdminFieldsEditor driverLogin={login} type={t} item={item} addNotification={addNotification}
                      onDone={() => { setEditing(''); load(); reload(); }} />
                )}

                {/* Structured visa-grant detail — edited independently of the notice. */}
                {t.key === 'visa' && (
                  <VisaDetailsPanel
                    initial={item?.fields?.visaDetails}
                    studentHint={!!(item?.fields?.vevo && (item.fields.vevo.subclass === '500' || (item.fields.vevo.conditions || []).map(String).includes('8105')))}
                    onSave={async (details) => {
                      const d = await call({ action: 'adminSaveVisaDetails', driverLogin: login, type: 'visa', visaDetails: details });
                      if (d && d.success) { addNotification?.('Visa details saved.', 'success'); await load(); reload(); }
                      else addNotification?.(d?.error || 'Could not save visa details', 'error');
                      return d;
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <DrawerUploads login={login} addNotification={addNotification} />

        <div style={{ marginTop: 18 }}>
          <button onClick={loadLog} style={ghostBtn}><History size={14} /> Access history</button>
          {showLog && (
            <div style={{ marginTop: 10, fontSize: '0.75rem', color: 'var(--hp-dim)', maxHeight: 240, overflowY: 'auto', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 10 }}>
              {log === null ? 'Loading…' : !log.length ? 'No access recorded.' : log.map((e, i) => (
                <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid var(--hp-border)' }}>
                  <b style={{ color: 'var(--hp-text)' }}>{e.actor_login}</b> — {e.action}{e.doc_type ? ` (${e.doc_type})` : ''} · {new Date(e.at).toLocaleString('en-AU')}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Contractor-upload helpers (driver-docs store) ────────────────────────────
function fmtUploadWhen(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

// A single uploaded-document row (used in the drawer + the Uploads tab).
function UploadRow({ u, showDriver, onChanged, addNotification }) {
  const [busy, setBusy] = useState('');
  const reviewed = u.status === 'reviewed';
  const view = async () => { setBusy('v'); await viewUploadInNewTab(u.id, (m) => addNotification?.(m, 'error')); setBusy(''); };
  const toggle = async () => {
    setBusy('r');
    const d = await callDocs({ action: 'markUploadReviewed', id: u.id, unreview: reviewed });
    setBusy('');
    if (d && d.success) onChanged?.(); else addNotification?.(d?.error || 'Could not update', 'error');
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)' }}>
      <FileText size={16} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--hp-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.title}</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
          {showDriver && <><span style={{ fontFamily: 'var(--font-mono)' }}>{u.driverLogin}</span> · </>}
          uploaded {fmtUploadWhen(u.uploadedAt)}{u.requestTitle ? ` · for "${u.requestTitle}"` : ''}
        </div>
      </div>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', fontWeight: 700, color: reviewed ? 'var(--hp-green)' : 'var(--hp-gold)', whiteSpace: 'nowrap' }}>
        {reviewed ? <><CheckCircle size={12} /> Reviewed</> : <><Clock size={12} /> New</>}
      </span>
      <button onClick={view} disabled={busy === 'v'} style={miniBtn}><Eye size={13} /> {busy === 'v' ? '…' : 'View'}</button>
      <button onClick={toggle} disabled={busy === 'r'} style={miniBtn}>{reviewed ? 'Unmark' : 'Mark reviewed'}</button>
    </div>
  );
}

// Request-a-document modal (preview email + SMS, then send) — mirrors the Documents
// page flow so the office can request from within a driver's drawer too.
function RequestDocModal({ login, onClose, onSent, addNotification }) {
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const doPreview = async () => {
    if (!title.trim()) { addNotification?.('Name the document you need', 'error'); return; }
    setBusy(true);
    const d = await callDocs({ action: 'previewUploadRequest', driverLogin: login, title: title.trim(), note: note.trim() });
    setBusy(false);
    if (d && d.success) setPreview(d); else addNotification?.(d?.error || 'Could not build preview', 'error');
  };
  const send = async () => {
    setBusy(true);
    const d = await callDocs({ action: 'requestUpload', driverLogin: login, title: title.trim(), note: note.trim() });
    setBusy(false);
    if (d && d.success) {
      const bits = [d.sent?.email ? 'email' : '', d.sent?.sms ? 'SMS' : ''].filter(Boolean).join(' + ');
      addNotification?.(`Requested “${title.trim()}” from ${login}${bits ? ` (${bits} sent)` : ' (portal only)'}`, 'success');
      onSent?.();
    } else addNotification?.(d?.error || 'Could not send request', 'error');
  };
  const inp = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.9rem', boxSizing: 'border-box' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1400, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px 12px', overflowY: 'auto' }}>
      <div style={{ width: 560, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderRadius: 14, border: '1px solid var(--hp-border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1.02rem' }}>Request a document from {login}</h3>
          <button onClick={onClose} style={ghostBtn} disabled={busy}><X size={16} /></button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={drawerLbl}>Document requested</label>
            <input style={inp} placeholder="e.g. Police check" value={title} onChange={e => { setTitle(e.target.value); setPreview(null); }} />
          </div>
          <div>
            <label style={drawerLbl}>Note (optional)</label>
            <textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} value={note} onChange={e => { setNote(e.target.value); setPreview(null); }} />
          </div>
          {preview && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {preview.warnings?.length > 0 && <div style={{ fontSize: '0.8rem', color: 'var(--hp-red)' }}>{preview.warnings.join(' ')}</div>}
              <div>
                <div style={{ ...drawerLbl, display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={12} /> Email {preview.email?.to ? `→ ${preview.email.to}` : '— skipped'}</div>
                {preview.email?.to && <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 10, fontSize: '0.8rem', background: 'var(--hp-bg)' }}><div style={{ fontWeight: 700, marginBottom: 4 }}>{preview.email.subject}</div><div dangerouslySetInnerHTML={{ __html: preview.email.html }} /></div>}
              </div>
              <div>
                <div style={{ ...drawerLbl, display: 'flex', alignItems: 'center', gap: 6 }}><MessageSquare size={12} /> SMS {preview.sms?.to ? `→ ${preview.sms.to}` : '— skipped'}</div>
                {preview.sms?.to && <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 10, fontSize: '0.8rem', background: 'var(--hp-bg)', whiteSpace: 'pre-wrap' }}>{preview.sms.body}</div>}
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--hp-border)' }}>
          {preview ? (
            <button style={{ ...miniBtn, flex: 1, justifyContent: 'center', padding: '10px' }} disabled={busy} onClick={send}>{busy ? 'Sending…' : 'Send request'}</button>
          ) : (
            <button style={{ ...miniBtn, flex: 1, justifyContent: 'center', padding: '10px' }} disabled={busy} onClick={doPreview}>{busy ? 'Working…' : 'Preview request'}</button>
          )}
          <button style={ghostBtn} onClick={onClose} disabled={busy}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Uploaded documents inside a driver's drawer + a "request a document" button.
function DrawerUploads({ login, addNotification }) {
  const [uploads, setUploads] = useState(null);
  const [asking, setAsking] = useState(false);
  const load = useCallback(async () => {
    const d = await callDocs({ action: 'listUploads', driverLogin: login });
    setUploads(d && d.success ? (d.uploads || []) : []);
  }, [login]);
  useEffect(() => { load(); }, [load]);
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <h3 style={{ ...sectionH, margin: 0, fontSize: '0.95rem' }}>Uploaded documents</h3>
        <button onClick={() => setAsking(true)} style={miniBtn}><UploadCloud size={13} /> Request a document</button>
      </div>
      {uploads === null ? (
        <div style={{ color: 'var(--hp-dim)', fontSize: '0.8rem' }}>Loading…</div>
      ) : !uploads.length ? (
        <div style={{ color: 'var(--hp-dim)', fontSize: '0.8rem' }}>Nothing uploaded yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {uploads.map(u => <UploadRow key={u.id} u={u} onChanged={load} addNotification={addNotification} />)}
        </div>
      )}
      {asking && <RequestDocModal login={login} onClose={() => setAsking(false)} onSent={() => { setAsking(false); load(); }} addNotification={addNotification} />}
    </div>
  );
}

// Uploads tab — every contractor upload across the fleet, newest first.
function UploadsTab({ addNotification, openDrawer }) {
  const [uploads, setUploads] = useState(null);
  const [onlyNew, setOnlyNew] = useState(false);
  const load = useCallback(async () => {
    const d = await callDocs({ action: 'listUploads' });
    setUploads(d && d.success ? (d.uploads || []) : []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const rows = (uploads || []).filter(u => !onlyNew || u.status !== 'reviewed');
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <h2 style={{ ...sectionH, marginBottom: 0 }}>Contractor uploads</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
          <input type="checkbox" checked={onlyNew} onChange={e => setOnlyNew(e.target.checked)} /> Only new
        </label>
        <button onClick={load} style={ghostBtn}><RefreshCw size={14} /> Refresh</button>
      </div>
      {uploads === null ? (
        <div style={{ color: 'var(--hp-dim)' }}><RefreshCw size={15} className="spin" /> Loading…</div>
      ) : !rows.length ? (
        <div style={{ color: 'var(--hp-dim)', fontSize: '0.85rem' }}>No uploads{onlyNew ? ' awaiting review' : ' yet'}.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map(u => (
            <div key={u.id} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <UploadRow u={u} showDriver onChanged={load} addNotification={addNotification} />
              <button onClick={() => openDrawer?.(u.driverLogin)} style={{ ...linkBtn, alignSelf: 'flex-start', marginTop: 2 }}>Open {u.driverLogin}'s profile →</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const drawerLbl = { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-dim)', marginBottom: 5, display: 'block' };
const linkBtn = { background: 'none', border: 'none', color: 'var(--hp-gold)', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', padding: '2px 0' };

// Work-rights chip on the visa item — derived server-side by _shared/work-rights.js.
const WORK_RIGHT = {
  unlimited:       { label: 'Unlimited work',  color: 'var(--hp-green)', bg: 'rgba(46,125,50,0.12)' },
  '48h_fortnight': { label: '48h / fortnight',  color: '#a67c00',        bg: 'rgba(201,168,76,0.16)' },
  no_work:         { label: 'No work',          color: 'var(--hp-red)',   bg: 'rgba(214,69,69,0.12)' },
  unknown:         { label: 'Needs review',     color: 'var(--hp-dim)',   bg: 'var(--hp-surface)' },
};
function WorkRightChip({ right, basis }) {
  const w = WORK_RIGHT[right] || WORK_RIGHT.unknown;
  return (
    <span title={basis || ''} style={{ padding: '2px 9px', borderRadius: 999, fontSize: '0.66rem', fontWeight: 800, color: w.color, background: w.bg, border: '1px solid transparent' }}>
      {w.label}
    </span>
  );
}

// ── bits ────────────────────────────────────────────────────────────────────────
function Tile({ label, value, color, icon }) {
  return (
    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '14px 18px', background: 'var(--hp-surface)', minWidth: 140 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--hp-dim)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{icon} {label}</div>
      <div style={{ fontSize: '1.8rem', fontWeight: 800, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}
function StatusChip({ status, optional }) {
  const s = DMS_STATUS[status] || DMS_STATUS.missing;
  const label = (status === 'missing' && optional) ? 'Optional' : s.label;
  return <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: '0.66rem', fontWeight: 800, color: s.color, background: s.bg, border: `1px solid ${s.bg === 'transparent' ? 'var(--hp-border)' : 'transparent'}` }}>{label}</span>;
}
function Legend() {
  const items = [['On file', 'ok'], ['Expiring', 'expiring'], ['Expired', 'expired'], ['Requested', 'renewal'], ['Missing', 'missing']];
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginLeft: 'auto' }}>
      {items.map(([lbl, k]) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: k === 'missing' ? 'transparent' : CELL_COLOR[k], border: k === 'missing' ? '1px solid var(--hp-border)' : 'none' }} /> {lbl}
        </span>
      ))}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
        <span style={{ width: 12, height: 12, borderRadius: 3, background: NOT_REQ_BG, border: `1px dashed ${NOT_REQ_BORDER}` }} /> Not required
      </span>
    </div>
  );
}

// ── Admin file uploader (on a driver's behalf) ────────────────────────────────
// Add file(s) by choose / photo / paste (⌘V) / drag-drop; OCR runs AUTOMATICALLY
// on each add and fills the number / card number / expiry. Two-sided cards (the
// licence: Front + Back) get labelled slots so the rear can be captured too — the
// back is where an NSW licence card number lives.
function AdminFileEditor({ driverLogin, type, addNotification, onDone }) {
  const cfg = DMS_TYPES.find((x) => x.key === type.key) || type;
  const sides = cfg.sides || null;                 // e.g. ['Front','Back'] for a licence
  const need = sides ? sides.length : 1;
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState('');            // '' | 'ocr' | 'save'
  const [docNumber, setDocNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [extra, setExtra] = useState({});
  const [tfn, setTfn] = useState('');              // captureTfn only — routed to the vault
  const [sourceType, setSourceType] = useState('');
  const [ocrStatus, setOcrStatus] = useState('manual');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const pagesRef = useRef([]); pagesRef.current = pages;

  const nextLabel = sides ? (sides[pages.length] || null) : null;

  // OCR every current page together and MERGE the result in (only non-empty values),
  // so reading the Front then the Back accumulates licence no. + expiry + card no.
  const runOcr = async (list) => {
    if (!list || !list.length) return;
    setBusy('ocr'); setNote('');
    try {
      const d = await call({ action: 'ocrDoc', type: type.key,
        pages: list.map((p) => ({ fileBase64: p.base64, contentType: p.contentType })), sideLabels: sides || [] });
      if (d && d.success) {
        if (d.docNumber) setDocNumber(d.docNumber);
        if (cfg.expiry && d.expiryDate) setExpiryDate(d.expiryDate);
        if (d.sourceType) setSourceType(d.sourceType);
        if (cfg.captureTfn && d.tfn) setTfn(d.tfn);
        setExtra((prev) => { const m = { ...prev }; Object.entries(d.fields || {}).forEach(([k, v]) => { if (v) m[k] = v; }); return m; });
        setOcrStatus('ok');
        const got = d.docNumber || d.expiryDate || (cfg.captureTfn && d.tfn) || Object.values(d.fields || {}).some(Boolean);
        setNote(got ? 'Read from the document — please check it’s right.' : 'Couldn’t find the details — enter them below.');
      } else { setNote(d?.error || 'Couldn’t read it — enter the details below.'); }
    } catch (ex) { setNote(ex.message); } finally { setBusy(''); }
  };

  // Normalise incoming file(s), append (respecting side slots), then auto-OCR.
  const addFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setErr('');
    const room = sides ? Math.max(0, need - pagesRef.current.length) : list.length;
    if (sides && room === 0) { setErr(`Both sides added — press Clear to start over.`); return; }
    const take = sides ? list.slice(0, room) : list;
    try {
      const added = [];
      for (const f of take) added.push(await fileToPage(f));
      const next = [...pagesRef.current, ...added];
      setPages(next);
      runOcr(next);
    } catch (ex) { setErr(ex.message); }
  };
  const addRef = useRef(addFiles); addRef.current = addFiles;

  // Paste an image anywhere while the editor is open (Cmd/Ctrl+V).
  useEffect(() => {
    const onPaste = (e) => {
      const items = (e.clipboardData && e.clipboardData.items) || [];
      const files = [];
      for (const it of items) if (it.kind === 'file' && it.type.startsWith('image/')) { const f = it.getAsFile(); if (f) files.push(f); }
      if (files.length) { e.preventDefault(); addRef.current(files); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  const pasteBtn = async () => {
    setErr('');
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) { setErr('Press Ctrl/Cmd+V to paste the image here.'); return; }
      const items = await navigator.clipboard.read();
      const files = [];
      for (const it of items) { const tp = it.types.find((t) => t.startsWith('image/')); if (tp) { const b = await it.getType(tp); files.push(new File([b], 'pasted.png', { type: tp })); } }
      if (files.length) addFiles(files); else setErr('No image found on the clipboard.');
    } catch { setErr('Couldn’t read the clipboard — press Ctrl/Cmd+V instead.'); }
  };

  // `input.files` is a LIVE FileList tied to the input — resetting `value` (so the same
  // file/photo can be picked twice in a row) EMPTIES the list you're already holding.
  // Snapshot the files into a real array first, or every Choose-file / Take-photo pick
  // silently arrives as zero files and nothing happens.
  const pick = (e) => { const f = Array.from(e.target.files || []); if (e.target) e.target.value = ''; addFiles(f); };

  const save = async () => {
    if (!pages.length) { setErr('Add a file first.'); return; }
    if (busy === 'ocr') { setErr('Still reading the document — one moment.'); return; }
    // Don't silently store an expiry document with nothing captured (the blank-save bug).
    if (cfg.expiry && !docNumber.trim() && !expiryDate &&
        !window.confirm(`No ${(cfg.numberLabel || 'number').toLowerCase()} or expiry has been entered for this ${cfg.label}. Save it anyway?`)) return;
    setBusy('save'); setErr('');
    try {
      const cleanTfn = (tfn || '').replace(/\s+/g, '');
      const d = await call({ action: 'adminUploadDoc', driverLogin, type: type.key,
        pages: pages.map((p) => ({ fileBase64: p.base64, contentType: p.contentType })), sideLabels: sides || [],
        filename: pages.length > 1 ? `${type.key}.pdf` : pages[0].filename,
        docNumber, expiryDate: cfg.expiry ? expiryDate : '', ocrStatus, fields: extra, sourceType,
        // captureTfn only — TFN goes to the encrypted vault server-side, never the doc.
        ...(cfg.captureTfn && /^\d{8,9}$/.test(cleanTfn) ? { tfn: cleanTfn } : {}) });
      if (!d || !d.success) throw new Error(d?.error || 'Upload failed');
      // The form saved; report the TFN outcome separately so a vault miss is visible.
      if (d.tfn && d.tfn.requested && !d.tfn.stored && d.tfn.error) {
        addNotification?.(d.tfn.error, 'error');
      } else if (d.tfn && d.tfn.stored) {
        addNotification?.(`${cfg.label} saved to ${driverLogin}; TFN stored securely (••• ${d.tfn.last3}).`, 'success');
      } else {
        addNotification?.(`${cfg.label} saved to ${driverLogin}.`, 'success');
      }
      onDone();
    } catch (ex) { setErr(ex.message); setBusy(''); }
  };

  return (
    <div style={editorWrap}>
      {err && <div style={{ color: 'var(--hp-red)', fontSize: '0.76rem' }}>{err}</div>}
      <div style={{ fontSize: '0.73rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>
        Add {cfg.label} for <b style={{ color: 'var(--hp-text)' }}>{driverLogin}</b>. {sides ? 'Front and back are both kept on file. ' : ''}Stored encrypted; {cfg.captureTfn
          ? ((cfg.extraFields || []).length
              ? 'the TFN is read off the form and kept in the secure vault (never on the document); the other details below are read off too — check them before saving.'
              : 'the TFN is read off the form and kept in the secure vault, never on the document itself.')
          : cfg.noNumber
            ? (cfg.extraFields || []).length
              ? 'the details below are read off the form automatically — check them before saving.'
              : 'no number is recorded from this form.'
            : 'the number and expiry are read automatically.'}
      </div>

      {/* Thumbnails, labelled by side */}
      {pages.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {pages.map((p, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ width: 64, height: 46, borderRadius: 6, border: '1px solid var(--hp-border)', overflow: 'hidden', background: 'var(--hp-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {p.contentType === 'application/pdf'
                  ? <FileText size={16} color="var(--hp-dim)" />
                  : <img alt="" src={`data:${p.contentType};base64,${p.base64}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              </div>
              {sides && <div style={{ fontSize: '0.6rem', color: 'var(--hp-dim)', marginTop: 2, fontWeight: 700 }}>{sides[i] || `Page ${i + 1}`}</div>}
            </div>
          ))}
          <button onClick={() => { setPages([]); setNote(''); }} style={miniBtn}><X size={12} /> Clear</button>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" multiple onChange={pick} style={{ display: 'none' }} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={pick} style={{ display: 'none' }} />

      {/* Drop / paste / choose zone (hidden once all sides are captured) */}
      {(!sides || pages.length < need) && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
          style={{ border: `1px dashed ${dragging ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 'var(--radius)', padding: '12px', textAlign: 'center', background: dragging ? 'rgba(201,168,76,0.08)' : 'transparent' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
            {sides && nextLabel
              ? <>Add the <b style={{ color: 'var(--hp-text)' }}>{nextLabel}</b> ({pages.length + 1} of {need}) — drop, paste (⌘V), or:</>
              : <>Drop an image or PDF, paste (⌘V), or:</>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button onClick={() => fileRef.current?.click()} style={miniBtn}><Upload size={12} /> Choose file</button>
            <button onClick={() => camRef.current?.click()} style={miniBtn}><Camera size={12} /> {sides && nextLabel ? `Take ${nextLabel.toLowerCase()} photo` : 'Take photo'}</button>
            <button onClick={pasteBtn} style={miniBtn}><FileText size={12} /> Paste image</button>
          </div>
        </div>
      )}

      {busy === 'ocr' && <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 6 }}><Loader size={13} /> Reading the document…</div>}
      {pages.length > 0 && busy !== 'ocr' && (
        <button onClick={() => runOcr(pages)} style={{ ...miniBtn, alignSelf: 'flex-start' }}><RefreshCw size={12} /> Re-read details</button>
      )}
      {note && <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{note}</div>}
      {sourceType === 'digital_screenshot' && (
        <div style={{ fontSize: '0.7rem', color: '#b8860b', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700 }}>
          <AlertTriangle size={11} /> Screenshot of a digital licence — not verified evidence
        </div>
      )}

      {/* Forms with no document reference (TFN declaration, super choice) hide this
          entirely — the only numbers on them are the person's own, and the generic
          read just returned the ATO's publication number. See noNumber in dmsTypes. */}
      {!cfg.noNumber && (
        <label style={editorLabel}>{cfg.numberLabel || 'Document number'}
          <input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} style={editorInput} />
        </label>
      )}
      {(cfg.extraFields || []).map((f) => (
        <label key={f.key} style={editorLabel}>{f.label}
          <input value={extra[f.key] || ''} placeholder={f.placeholder || ''} onChange={(e) => setExtra((x) => ({ ...x, [f.key]: e.target.value }))} style={editorInput} />
        </label>
      ))}
      {/* captureTfn: read off the TFN Declaration, confirmed here, then stored in the
          ENCRYPTED VAULT (not on the document). We only ever see it at capture time. */}
      {cfg.captureTfn && (
        <label style={editorLabel}>Tax File Number → secure vault
          <input value={tfn} onChange={(e) => setTfn(e.target.value.replace(/[^\d]/g, '').slice(0, 9))}
            inputMode="numeric" autoComplete="off" placeholder="8–9 digits, read off the form" style={editorInput} />
          <span style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', fontWeight: 400 }}>
            Check it against the form. Stored encrypted in the vault — never written onto this document.
          </span>
        </label>
      )}
      {cfg.expiry && (
        <label style={editorLabel}>Expiry date
          <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} style={editorInput} />
        </label>
      )}
      {sides && pages.length > 0 && pages.length < need && (
        <div style={{ fontSize: '0.72rem', color: '#b8860b', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600 }}>
          <AlertTriangle size={12} /> Add the {(sides[pages.length] || '').toLowerCase()} image too — we always keep the rear on file (most have nothing on the back, and that's fine).
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={save} disabled={busy === 'save' || busy === 'ocr' || !pages.length} style={{ ...primaryBtn, opacity: (busy === 'save' || busy === 'ocr' || !pages.length) ? 0.5 : 1 }}>
          {busy === 'save' ? <Loader size={13} /> : <Save size={13} />} {busy === 'save' ? 'Saving…' : busy === 'ocr' ? 'Reading…' : 'Save document'}
        </button>
        <button onClick={onDone} style={ghostBtn}><X size={13} /> Cancel</button>
      </div>
    </div>
  );
}

// ── Admin structured-record editor (company / address / emergency / scanner) ─────
function AdminFieldsEditor({ driverLogin, type, item, addNotification, onDone }) {
  const cfg = DMS_TYPES.find((x) => x.key === type.key) || type;
  const [vals, setVals] = useState(() => { const v = {}; (cfg.fields || []).forEach((f) => { v[f.key] = item?.fields?.[f.key] || ''; }); return v; });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const d = await call({ action: 'adminSaveFields', driverLogin, type: type.key, fields: vals });
      if (!d || !d.success) throw new Error(d?.error || 'Could not save');
      addNotification?.(`${cfg.label} saved to ${driverLogin}.`, 'success');
      onDone();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  return (
    <div style={editorWrap}>
      {err && <div style={{ color: 'var(--hp-red)', fontSize: '0.76rem', marginBottom: 8 }}>{err}</div>}
      {(cfg.fields || []).map((f) => (
        <label key={f.key} style={editorLabel}>{f.label}
          <input value={vals[f.key]} placeholder={f.placeholder || ''} onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))} style={editorInput} />
        </label>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={save} disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.5 : 1 }}>{busy ? <Loader size={13} /> : <Save size={13} />} {busy ? 'Saving…' : 'Save'}</button>
        <button onClick={onDone} style={ghostBtn}><X size={13} /> Cancel</button>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', fontSize: '0.82rem', fontWeight: 700,
      border: 'none', borderBottom: active ? '2px solid var(--hp-gold)' : '2px solid transparent',
      background: 'none', color: active ? 'var(--hp-text)' : 'var(--hp-dim)', cursor: 'pointer', marginBottom: -1,
    }}>{icon} {label}</button>
  );
}

// ── Requirements tab: pick a driver → tick which documents they must supply, then
// Save and/or Push the required-but-missing ones to their portal. ────────────────
function RequirementsTab({ data, reload, addNotification, openDrawer, initialDriver = '' }) {
  const drivers = data.drivers || [];
  const [filter, setFilter] = useState(initialDriver);
  // A deep link names the person; without it, start on whoever is first in the list.
  const [sel, setSel] = useState(initialDriver || drivers[0]?.login || null);
  const [checked, setChecked] = useState(null);   // Set of type keys for the selected driver
  const [busy, setBusy] = useState('');
  const [mobile, setMobile] = useState('');       // editable mobile for the selected person

  const selDriver = drivers.find((d) => d.login === sel) || null;

  // Seed the checkboxes from the driver's effective required set when the selection
  // changes. Intentionally keyed on `sel` only, so a reload() after Save/Push does
  // NOT wipe the admin's in-progress ticks.
  useEffect(() => {
    setChecked(selDriver ? new Set(selDriver.required || []) : null);
    setMobile(selDriver ? (selDriver.mobile || '') : '');
  }, [sel]);  // keyed on `sel` only, so reload() after Save/Push won't wipe in-progress ticks

  // Save the mobile to this person's registry (drivers.mobile / sorters.mobile) — the same
  // number the shift-finish SMS + document reminders read.
  const mobileDirty = !!selDriver && mobile.trim() !== (selDriver.mobile || '').trim();
  const saveMobile = async () => {
    if (!selDriver) return;
    setBusy('mobile');
    try {
      const d = await call({ action: 'setPersonMobile', audience: data.audience, driverLogin: selDriver.login, mobile: mobile.trim() });
      if (!d || !d.success) throw new Error(d?.error || 'Could not save mobile');
      addNotification?.(mobile.trim() ? `Mobile saved for ${selDriver.name}.` : `Mobile cleared for ${selDriver.name}.`, 'success');
      await reload();
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };

  const list = useMemo(() => {
    if (!filter) return drivers;
    const f = filter.toLowerCase();
    return drivers.filter((d) => d.login.toLowerCase().includes(f) || (d.name || '').toLowerCase().includes(f));
  }, [drivers, filter]);

  const toggle = (key) => setChecked((prev) => {
    const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n;
  });

  const dirty = !!(checked && selDriver && (
    checked.size !== (selDriver.required || []).length ||
    [...checked].some((k) => !(selDriver.required || []).includes(k))
  ));

  const saveTypes = async () => {
    const d = await call({ action: 'setRequirements', driverLogin: selDriver.login, types: [...checked] });
    if (!d || !d.success) throw new Error(d?.error || 'Could not save requirements');
  };

  const save = async () => {
    if (!selDriver || !checked) return;
    setBusy('save');
    try { await saveTypes(); addNotification?.('Requirements saved.', 'success'); await reload(); }
    catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };

  const push = async () => {
    if (!selDriver || !checked) return;
    setBusy('push');
    try {
      if (dirty) await saveTypes();   // push uses the latest ticked set
      const d = await call({ action: 'pushRequirements', driverLogin: selDriver.login });
      if (!d || !d.success) throw new Error(d?.error || 'Could not push to portal');
      addNotification?.(d.pushed
        ? `Requested ${d.pushed} item${d.pushed === 1 ? '' : 's'} — ${selDriver.name} will see them in My Documents.`
        : `${selDriver.name} already has everything required on file.`, 'success');
      await reload();
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };

  return (
    <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* Personnel list */}
      <div style={{ width: 260, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '6px 9px', background: 'var(--hp-surface)', marginBottom: 8 }}>
          <Search size={13} color="var(--hp-dim)" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter personnel…"
            style={{ border: 'none', background: 'none', color: 'var(--hp-text)', fontSize: '0.82rem', outline: 'none', width: '100%' }} />
        </div>
        <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', maxHeight: 560, overflowY: 'auto' }}>
          {list.map((d) => {
            const attn = (d.counts?.expired || 0) + (d.counts?.expiring || 0) + (d.counts?.renewal || 0) + (d.counts?.missing || 0);
            return (
              <button key={d.login} onClick={() => setSel(d.login)} style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '9px 11px',
                border: 'none', borderBottom: '1px solid var(--hp-border)', cursor: 'pointer',
                background: d.login === sel ? 'var(--hp-surface)' : 'transparent',
                borderLeft: `3px solid ${d.login === sel ? 'var(--hp-gold)' : 'transparent'}`,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--hp-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>{d.login}</div>
                </div>
                {attn > 0 && <span style={{ fontSize: '0.66rem', fontWeight: 800, color: 'var(--hp-red)', background: 'rgba(214,69,69,0.10)', borderRadius: 999, padding: '1px 7px' }}>{attn}</span>}
              </button>
            );
          })}
          {!list.length && <div style={{ padding: 14, color: 'var(--hp-dim)', fontSize: '0.8rem' }}>No personnel match.</div>}
        </div>
      </div>

      {/* Requirement editor */}
      <div style={{ flex: 1, minWidth: 320 }}>
        {!selDriver ? (
          <div style={{ color: 'var(--hp-dim)', fontSize: '0.85rem', padding: 20 }}>Select someone to set which documents they must supply.</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
              <h2 style={{ ...sectionH, marginBottom: 0 }}>{selDriver.name}</h2>
              <span style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>{selDriver.login}</span>
              <button onClick={() => openDrawer(selDriver.login)} style={{ ...miniBtn, marginLeft: 'auto' }}><Eye size={12} /> View documents</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '8px 0 14px' }}>
              <MessageSquare size={13} color="var(--hp-dim)" />
              <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>Mobile</span>
              <input value={mobile} onChange={e => setMobile(e.target.value)} placeholder="04xx xxx xxx" aria-label="Mobile number"
                onKeyDown={e => { if (e.key === 'Enter' && mobileDirty) saveMobile(); }}
                style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.82rem', outline: 'none', padding: '6px 9px', width: 160 }} />
              {mobileDirty && <button onClick={saveMobile} disabled={busy === 'mobile'} style={{ ...miniBtn, borderColor: 'var(--hp-gold)', color: 'var(--hp-gold)' }}>{busy === 'mobile' ? <Loader size={12} /> : <Save size={12} />} Save</button>}
              <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>Used for the shift-finish SMS &amp; document reminders.</span>
            </div>
            <p style={{ color: 'var(--hp-dim)', fontSize: '0.8rem', margin: '0 0 16px' }}>
              Tick the documents this person must keep on file. <b>Push to portal</b> asks them (red dot in My Documents + the daily reminder) for anything ticked that isn't already current.
            </p>

            {DMS_GROUPS.map((group) => {
              const groupTypes = DMS_TYPES.filter((t) => t.group === group);
              if (!groupTypes.length) return null;
              return (
                <div key={group} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 8 }}>{group}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {groupTypes.map((t) => {
                      const on = !!(checked && checked.has(t.key));
                      const cell = selDriver.items[t.key] || { status: 'missing' };
                      return (
                        <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 11px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: on ? 'var(--hp-surface)' : 'transparent', cursor: 'pointer' }}>
                          <input type="checkbox" checked={on} onChange={() => toggle(t.key)} style={{ width: 16, height: 16, accentColor: 'var(--hp-gold)', cursor: 'pointer' }} />
                          <span style={{ flex: 1, fontSize: '0.84rem', fontWeight: 600, color: on ? 'var(--hp-text)' : 'var(--hp-dim)' }}>
                            {t.label}{t.optional ? ' *' : ''}
                          </span>
                          <StatusChip status={cell.status} optional={t.optional} />
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
              <button onClick={save} disabled={!!busy || !dirty} style={{ ...primaryBtn, opacity: (!dirty || busy) ? 0.5 : 1 }}>
                <Save size={14} /> {busy === 'save' ? 'Saving…' : dirty ? 'Save requirements' : 'Saved'}
              </button>
              <button onClick={push} disabled={!!busy} style={{ ...ghostBtn, opacity: busy ? 0.6 : 1 }}>
                <UploadCloud size={14} /> {busy === 'push' ? 'Pushing…' : 'Push required to portal'}
              </button>
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 8 }}>* optional — only tick if it applies to this person.</div>
          </>
        )}
      </div>
    </div>
  );
}

// ── By-document-type tab: one document class across everyone required to hold it —
// the class log, with status, expiry, masked number and an audited View. ─────────
function ByTypeTab({ data, reload, addNotification, openDrawer }) {
  const drivers = data.drivers || [];
  const [typeKey, setTypeKey] = useState(DMS_TYPES[0].key);
  const [busy, setBusy] = useState('');
  const t = DMS_TYPES.find((x) => x.key === typeKey) || DMS_TYPES[0];

  const rows = useMemo(() => {
    const rank = { expired: 0, renewal: 1, expiring: 2, missing: 3, ok: 4 };
    return drivers
      .filter((d) => (d.required || []).includes(typeKey))
      .map((d) => ({ login: d.login, name: d.name, cell: d.items[typeKey] || { status: 'missing' } }))
      .sort((a, b) => {
        const ra = rank[a.cell.status] ?? 9, rb = rank[b.cell.status] ?? 9;
        if (ra !== rb) return ra - rb;
        return (a.cell.expiryDate || '9999').localeCompare(b.cell.expiryDate || '9999') || (a.name || '').localeCompare(b.name || '');
      });
  }, [drivers, typeKey]);

  const summary = useMemo(() => {
    const c = { ok: 0, expiring: 0, expired: 0, renewal: 0, missing: 0 };
    rows.forEach((r) => { c[r.cell.status] = (c[r.cell.status] || 0) + 1; });
    return c;
  }, [rows]);

  const view = async (id) => { setBusy(id + ':v'); await viewDocInNewTab(id, (m) => addNotification?.(m, 'error')); setBusy(''); };
  const requestRenewal = async (login) => {
    setBusy(login + ':r');
    try {
      const d = await call({ action: 'requestRenewal', driverLogin: login, type: typeKey });
      if (!d || !d.success) throw new Error(d?.error || 'Failed');
      addNotification?.('Renewal requested — the driver will be notified.', 'success');
      await reload();
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };
  const checkRego = async (login) => {
    setBusy(login + ':c');
    try {
      const d = await call({ action: 'checkRego', driverLogin: login, type: typeKey });
      if (!d || !d.success) throw new Error(d?.error || 'Rego check failed');
      addNotification?.(d.valid ? `${d.plate}: Registered.` : `${d.plate}: ${d.regoStatus || 'not registered'}.`, d.valid ? 'success' : 'error');
      await reload();
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(''); }
  };

  return (
    <div>
      {/* Class picker */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        {DMS_TYPES.map((x) => (
          <button key={x.key} onClick={() => setTypeKey(x.key)} style={{
            padding: '7px 12px', fontSize: '0.78rem', fontWeight: 700, borderRadius: 999, cursor: 'pointer',
            border: `1px solid ${x.key === typeKey ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
            background: x.key === typeKey ? 'rgba(201,168,76,0.14)' : 'var(--hp-surface)',
            color: x.key === typeKey ? 'var(--hp-text)' : 'var(--hp-dim)',
          }}>{x.label}</button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ ...sectionH, marginBottom: 0 }}>{t.label}</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[['On file', 'ok'], ['Expiring', 'expiring'], ['Expired', 'expired'], ['Requested', 'renewal'], ['Missing', 'missing']].map(([lbl, k]) => (
            summary[k] ? <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', color: 'var(--hp-dim)' }}>
              <span style={{ width: 11, height: 11, borderRadius: 3, background: k === 'missing' ? 'transparent' : CELL_COLOR[k], border: k === 'missing' ? '1px solid var(--hp-border)' : 'none' }} />
              {summary[k]} {lbl}
            </span> : null
          ))}
        </div>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.82rem' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>Personnel</th>
              <th style={th}>Status</th>
              {t.kind === 'file' && <th style={{ ...th, textAlign: 'left' }}>{t.numberLabel || 'Number'}</th>}
              {t.expiry && <th style={{ ...th, textAlign: 'left' }}>Expiry</th>}
              <th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const dU = daysUntil(r.cell.expiryDate);
              return (
                <tr key={r.login}>
                  <td style={{ ...td, textAlign: 'left' }}>
                    <button onClick={() => openDrawer(r.login)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--hp-text)', fontSize: '0.82rem' }}>{r.name}</button>
                    <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)' }}>{r.login}</div>
                  </td>
                  <td style={td}><StatusChip status={r.cell.status} /></td>
                  {t.kind === 'file' && <td style={{ ...td, textAlign: 'left', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono, monospace)' }}>{r.cell.docNumber || '—'}</td>}
                  {t.expiry && (
                    <td style={{ ...td, textAlign: 'left', color: r.cell.status === 'expired' ? 'var(--hp-red)' : r.cell.status === 'expiring' ? '#b8860b' : 'var(--hp-dim)' }}>
                      {r.cell.expiryDate ? `${fmtDate(r.cell.expiryDate)}${dU != null && r.cell.status !== 'ok' ? ` (${dU < 0 ? `${-dU}d ago` : `${dU}d`})` : ''}` : '—'}
                    </td>
                  )}
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {t.onlineCheck ? (
                      <button onClick={() => checkRego(r.login)} disabled={busy === r.login + ':c'} style={miniBtn}><RefreshCw size={12} /> {busy === r.login + ':c' ? '…' : (r.cell.fields?.checkedAt ? 'Re-check' : 'Check rego')}</button>
                    ) : (
                      <>
                        {r.cell.hasFile && <button onClick={() => view(r.cell.id)} disabled={busy === r.cell.id + ':v'} style={miniBtn}><Eye size={12} /> {busy === r.cell.id + ':v' ? '…' : 'View'}</button>}
                        {t.kind === 'file' && r.cell.status !== 'renewal' && (
                          <button onClick={() => requestRenewal(r.login)} disabled={busy === r.login + ':r'} style={{ ...miniBtn, marginLeft: 6 }}><Send size={12} /> {busy === r.login + ':r' ? '…' : 'Request'}</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={5} style={{ ...td, color: 'var(--hp-dim)', padding: 20 }}>No personnel require this document.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const sectionH = { fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--hp-text)', marginBottom: 12 };
const th = { padding: '8px 10px', fontSize: '0.72rem', color: 'var(--hp-dim)', fontWeight: 700, borderBottom: '1px solid var(--hp-border)', textAlign: 'center' };
const td = { padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid var(--hp-border)' };
const ghostBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: '0.8rem', fontWeight: 700, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', color: 'var(--hp-text)', cursor: 'pointer' };
const miniBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: '0.74rem', fontWeight: 700, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)', cursor: 'pointer' };
const primaryBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: '0.82rem', fontWeight: 800, border: 'none', borderRadius: 'var(--radius)', background: 'var(--hp-gold)', color: '#1a1a2e', cursor: 'pointer' };
const editorWrap = { marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--hp-border)', display: 'flex', flexDirection: 'column', gap: 8 };
const editorLabel = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', letterSpacing: '0.02em' };
const editorInput = { width: '100%', padding: '8px 10px', fontSize: '0.84rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)', boxSizing: 'border-box' };
